import React, { FC, ReactNode, useEffect } from 'react';
import { hasAccessByRole } from '../../helpers';
import { NotAuthorized } from '../NotAuthorized/NotAuthorized';
import { useSelector } from 'react-redux';
import { authSelectors } from '../../store';
import { hasRole } from '@clatter/platform';

export interface AccessGuardProps {
  allowedRoles?: string[];
  accessRole?: string | string[] | null;
  enableAccess?: boolean;
  fallback?: ReactNode;
  children?: ReactNode;
  user?: Auth0User | null;
  onChange?: (data: any) => void;
}

export interface AccessCheckParams {
  allowedRoles?: string[];
  accessRole?: string | string[] | null;
  enableAccess?: boolean;
  user?: Auth0User | null;
}

function checkAccess({ allowedRoles = [], accessRole, enableAccess = false, user }: AccessCheckParams): boolean {
  if (enableAccess) {
    return true;
  }
  // roles like clatter admin needs to have priority
  if (
    user &&
    allowedRoles.some((role) => {
      return hasRole(role, user);
    })
  ) {
    return true;
  }

  // acls
  if (accessRole) {
    if (Array.isArray(accessRole)) {
      if (accessRole.some((role) => hasAccessByRole({ allowedRoles, role }))) {
        return true;
      }
    } else if (hasAccessByRole({ allowedRoles, role: accessRole })) {
      return true;
    }
  }

  return false;
}

const AccessGuardComponent: FC<AccessGuardProps> = ({
  allowedRoles = [],
  accessRole = '',
  enableAccess = false,
  fallback = <NotAuthorized />,
  user = null,
  children,
  onChange,
}) => {
  const activeUser = user || useSelector(authSelectors.getUserAuthState)?.user;

  if (!activeUser) {
    console.warn('Access Guard: no active user found!');
  }

  const isAllowed = checkAccess({
    allowedRoles,
    accessRole,
    enableAccess,
    user: activeUser,
  });

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange({ isAllowed });
    }
  }, [isAllowed]);

  return isAllowed ? <>{children}</> : <>{fallback}</>;
};

// Note: For this method to work properly, it needs to be passed a "user".
// AccessGuardComponent gets the user from the store, but this method is meant
// to be used outside the component tree, so "user" can be undefined.
export const AccessGuard = Object.assign(AccessGuardComponent, {
  check: checkAccess,
});
